import { useCallback, useEffect, useMemo, useState } from 'react';
import useQueryParams from 'hooks/useQueryParams';
import usePageParams from 'hooks/usePageParams';
import { EMPTY_ARRAY, FIRST_PAGE, PAGE_PARAM_KEYWORD } from 'data/global';
import { getArrayLength } from 'utils';

type PendingPage<T> = {
  page: number;
  rows: T[];
};

type UseDrawerNavigationOptions<T> = {
  rows: T[];
  selectedId?: string;
  totalPages?: number;
  pageParam?: string;
  isLoading?: boolean;
  getIsNavigable?: (row: T) => boolean;
  onNavigate: (row: T) => void;
};

export default function useDrawerNavigation<T extends { _id?: string }>({
  rows = EMPTY_ARRAY,
  selectedId,
  isLoading,
  totalPages = 1,
  pageParam = PAGE_PARAM_KEYWORD,
  getIsNavigable,
  onNavigate,
}: UseDrawerNavigationOptions<T>) {
  const { setQueryParam } = useQueryParams();

  const page = +usePageParams(pageParam);

  const [pendingPage, setPendingPage] = useState<PendingPage<T>>();

  const navigableRows = useMemo(
    () => (getIsNavigable ? rows.filter(getIsNavigable) : rows),
    [getIsNavigable, rows],
  );

  const selectedIndex = useMemo(
    () => navigableRows.findIndex(({ _id }) => _id === selectedId),
    [navigableRows, selectedId],
  );

  const isSelectedOnPage = selectedIndex >= 0;

  const prevRow = useMemo(
    () => (isSelectedOnPage ? navigableRows[selectedIndex - 1] : undefined),
    [isSelectedOnPage, navigableRows, selectedIndex],
  );

  const nextRow = useMemo(
    () => (isSelectedOnPage ? navigableRows[selectedIndex + 1] : undefined),
    [isSelectedOnPage, navigableRows, selectedIndex],
  );

  const hasPrev = isSelectedOnPage && (!!prevRow || page > +FIRST_PAGE);
  const hasNext = isSelectedOnPage && (!!nextRow || page < totalPages);

  const goToPage = useCallback(
    (newPage: number) => {
      if (pendingPage) return;

      setPendingPage({ page, rows });
      setQueryParam(pageParam, newPage);
    },
    [page, pageParam, pendingPage, rows, setQueryParam],
  );

  const onPrev = useCallback(
    () => (prevRow ? onNavigate(prevRow) : goToPage(page - 1)),
    [goToPage, onNavigate, page, prevRow],
  );

  const onNext = useCallback(
    () => (nextRow ? onNavigate(nextRow) : goToPage(page + 1)),
    [goToPage, nextRow, onNavigate, page],
  );

  useEffect(() => {
    if (!pendingPage || isLoading || rows === pendingPage.rows) return;

    const row =
      page > pendingPage.page
        ? navigableRows[0]
        : navigableRows[getArrayLength(navigableRows) - 1];

    setPendingPage(undefined);

    if (row) onNavigate(row);
  }, [isLoading, navigableRows, onNavigate, page, pendingPage, rows]);

  return {
    ...(hasPrev && { onPrev }),
    ...(hasNext && { onNext }),
  };
}
